import React from 'react';
import { Content, ExpandableSection, List, ListComponent, ListItem, OrderType } from '@patternfly/react-core';
import { CopyableCodeBlock } from './CopyableCodeBlock';

interface DeployInstructionsDebianProps {
  envFileContent: string;
  hasCredentials: boolean;
}

/**
 * The Debian-based (APT) deployment instructions for the Linux PHP agent, in the "Debian based"
 * platform tab. The .deb ships from the Ancalagon flat APT repository (the trailing `./` in the
 * sources line marks a flat repo — apt fails without it) and runs on the distribution's stock
 * PHP (floor 8.1), so unlike the RPM there is no PHP 8.5 requirement; the Sury step is strictly
 * optional and stays collapsed.
 */
export const DeployInstructionsDebian: React.FC<DeployInstructionsDebianProps> = ({
  envFileContent,
  hasCredentials,
}) => (
  <>
    <Content component="p">
      Run these steps as root on each server that should report to Ancalagon Manager. The agent is read-only: it only
      collects and transmits system information, and never modifies the host.
    </Content>
    <List component={ListComponent.ol} type={OrderType.number}>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Install the agent.</strong> Trust the Ancalagon signing key, add the APT repository, and install the
          package. The agent runs on the distribution&apos;s stock PHP (Debian 12 ships 8.2, Debian 13 ships 8.4 —
          anything ≥ 8.1 works), and the required PHP extensions are installed automatically; unlike RHEL, libvirt is
          not needed on Debian hosts:
        </Content>
        <CopyableCodeBlock
          id="agent-deb-install-copy"
          code={[
            'curl -fsSL https://repo.ancalagon.be/common/deb/ancalagon.gpg -o /usr/share/keyrings/ancalagon.gpg',
            "echo 'deb [signed-by=/usr/share/keyrings/ancalagon.gpg] https://repo.ancalagon.be/common/deb ./' > /etc/apt/sources.list.d/ancalagon.list",
            'apt update',
            'apt install manager-agent',
          ].join('\n')}
          copyLabel="Copy install commands"
        />
      </ListItem>
      <ListItem className="pf-v6-u-mb-md">
        <ExpandableSection toggleText="Optional: install PHP 8.5 from the Sury repository">
          <Content component="p" className="pf-v6-u-mb-sm">
            The agent does not require it — only for admins who want the latest PHP on Debian:
          </Content>
          <CopyableCodeBlock
            id="agent-deb-sury-copy"
            code={[
              'apt install -y lsb-release ca-certificates curl',
              'curl -sSLo /tmp/debsuryorg-archive-keyring.deb https://packages.sury.org/debsuryorg-archive-keyring.deb',
              'dpkg -i /tmp/debsuryorg-archive-keyring.deb',
              'echo "deb [signed-by=/usr/share/keyrings/deb.sury.org-php.gpg] https://packages.sury.org/php/ $(lsb_release -sc) main" > /etc/apt/sources.list.d/php-sury.list',
              'apt update',
              'apt install php8.5-cli php8.5-curl php8.5-xml',
            ].join('\n')}
            copyLabel="Copy Sury PHP commands"
          />
          <Content component="small">
            On Ubuntu, use <code>add-apt-repository ppa:ondrej/php</code> instead of the Sury Debian repo.
          </Content>
        </ExpandableSection>
      </ListItem>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Configure the agent.</strong> Write this configuration — already filled in for your organization —
          to <code>/etc/ancalagon/manager-agent.env</code>:
        </Content>
        <CopyableCodeBlock id="agent-deb-env-copy" code={envFileContent} copyLabel="Copy configuration" />
        {!hasCredentials && (
          <Content component="small">
            Replace <code>&lt;your-client-secret&gt;</code> with the client secret you saved when the agent was
            enabled.
          </Content>
        )}
        <Content component="small">
          Prefer a guided setup? Run <code>php /opt/ancalagon/manager-agent/bin/agent.php --setup</code> and enter the
          values above when prompted.
        </Content>
      </ListItem>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Test the agent.</strong> Do a dry run first (collects and prints the report without sending
          anything), then send a first real report:
        </Content>
        <CopyableCodeBlock
          id="agent-deb-test-copy"
          code={
            'php /opt/ancalagon/manager-agent/bin/agent.php --dry-run\nphp /opt/ancalagon/manager-agent/bin/agent.php -v'
          }
          copyLabel="Copy test commands"
        />
      </ListItem>
      <ListItem>
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Enable hourly reporting.</strong> Turn on the systemd timer so the agent reports every hour, and
          check its status:
        </Content>
        <CopyableCodeBlock
          id="agent-deb-timer-copy"
          code={'manager-agent --set-auto\nsystemctl status manager-agent.timer'}
          copyLabel="Copy timer commands"
        />
      </ListItem>
    </List>
  </>
);
